import axios, { AxiosError } from "axios";

import Store, { store } from "../redux/index";
import checkExists from "../utils/checks/checkExists";
import routes from "../routes/routes";
import { clearToken } from "../redux/auth/actions";
import { clearUserID } from "../redux/userID/actions";

export const baseURL = {
  ping: "https://google.com",
  core: process.env.REACTWEB_COREAPI_URL,
};

function isNetworkFailure(error: AxiosError): boolean {
  if (error.message === "Network Error") {
    return true;
  }

  if (!error.response) {
    return true;
  }

  const networkErrorCodes = ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"];
  return networkErrorCodes.includes(error.code ?? "");
}

const SESSION_VALIDATION_ENDPOINTS = ["v3/auth/profile"];

function isSessionValidationEndpoint(requestUrl: string): boolean {
  return SESSION_VALIDATION_ENDPOINTS.some((endpoint) =>
    requestUrl.includes(endpoint)
  );
}

function shouldLogoutOnUnauthorized(error: AxiosError): boolean {
  if (error.response?.status !== 401) {
    return false;
  }

  if (isNetworkFailure(error)) {
    return false;
  }

  const requestUrl = error.config?.url ?? "";

  if (
    requestUrl.includes("v3/auth/login") ||
    requestUrl.includes("v3/auth/password")
  ) {
    return false;
  }

  if (!isSessionValidationEndpoint(requestUrl)) {
    return false;
  }

  const authHeader =
    error.config?.headers?.Authorization ?? error.config?.headers?.authorization;

  if (!authHeader) {
    return false;
  }

  return checkExists(getAuthorization());
}

export const coreHTTPClient = axios.create({
  baseURL: baseURL.core,
  responseType: "json",
  timeout: 90000,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFTOKEN",
});

coreHTTPClient.interceptors.request.use(
  (config) => {
    if (checkExists(getAuthorization())) {
      config.headers.Authorization = `Bearer ${getAuthorization()}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

coreHTTPClient.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (axios.isCancel(error)) {
      throw error;
    }

    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    if (shouldLogoutOnUnauthorized(error)) {
      store.dispatch(clearToken());
      store.dispatch(clearUserID());
      window.location.href = routes.AUTH.LOGIN_UNAUTHORIZED;
    }

    return Promise.reject(error);
  }
);

export const coreHTTPClientOld = axios.create({
  baseURL: baseURL.core,
  responseType: "json",
  timeout: 90000,
});

coreHTTPClientOld.interceptors.request.use(
  (config) => {
    if (checkExists(getAuthorization())) {
      config.headers.Authorization = `Token ${getAuthorization()}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

export function getAuthorization() {
  return Store().store.getState().auth.token;
}
